import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputLabel,
  ListItemText,
  MenuItem,
  Select,
  TextField,
} from "@mui/material";
import { ChangeEvent, useContext, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { ReleaseNoteType } from "components/release";
import { uploadResourceFiles } from "api/layer";
import { AppAction, AppContext } from "helpers/context";
// Version input is handled as plain string for now

interface Props {
  open: boolean;
  onClose: () => void;
  onSave: (data: ReleaseNoteType) => void;
  data?: ReleaseNoteType;
  uploading?: boolean;
  fileIconGetter?: (type: string) => string;
}

type FormDataInternal = ReleaseNoteType;

function ReleaseEditModal(props: Props) {
  const [t] = useTranslation();
  const [, dispatch] = useContext(AppContext);
  const [isError, setIsError] = useState(false);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const [attachments, setAttachments] = useState<string[]>(
    props.data?.attachments || []
  );
  const [uploading, setUploading] = useState(false);

  const [formData, setFormData] = useState<FormDataInternal>(
    props.data
      ? {
          ...props.data,
          status: props.data.status || "draft",
        }
      : {
          id: "",
          title: "",
          version: "",
          content: "",
          status: "draft",
        }
  );

  const handleChange = (event: any) => {
    const { name, value } = event.target;
    setFormData((prevData: any) => ({
      ...prevData,
      [name]: value,
    }));
  };

  const handleUploadClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = async (event: ChangeEvent<HTMLInputElement>) => {
    const { files } = event.target;
    if (!files || files.length === 0) return;

    const formDataUpload = new FormData();
    Array.from(files as ArrayLike<File>).forEach((file) => {
      formDataUpload.append("files", file);
    });

    setUploading(true);
    try {
      const res: any = await uploadResourceFiles(formDataUpload);
      const filePath = res?.data;
      if (filePath) {
        const paths = Array.isArray(filePath) ? filePath : [filePath];
        setAttachments((prev) => [...prev, ...paths]);
      }
    } catch (error: any) {
      console.error("Failed to upload files:", error);
      const status = error?.response?.status || error?.status;
      dispatch({
        type: AppAction.addSnackBarParams,
        payload: {
          title: "uploadFile",
          titleIconColor: "red",
          titleIcon: "error",
          resMessage:
            status === 413
              ? t("snackBar.fileTooLarge")
              : t("snackBar.uploadFailed"),
        },
      });
    } finally {
      setUploading(false);
    }

    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const close = () => {
    props.onClose();
  };

  const handleSave = () => {
    // Validate required fields
    if (!formData.title || !formData.version || !formData.content) {
      setIsError(true);
      return;
    }

    const data: ReleaseNoteType = {
      ...formData,
      id: formData.id || crypto.randomUUID(),
      version: formData.version,
      attachments,
    };

    props.onSave(data);
    props.onClose();
  };

  const reset = () => {
    setFormData(
      props.data
        ? {
            ...props.data,
            status: props.data.status || "draft",
          }
        : {
            id: "",
            title: "",
            version: "",
            content: "",
            status: "draft",
          }
    );
    setAttachments(props.data?.attachments || []);
    setIsError(false);
  };

  const getFileIcon = (type: string) => {
    if (props.fileIconGetter) return props.fileIconGetter(type);
    if (!type) return "insert_drive_file";
    if (type === "pdf") return "picture_as_pdf";
    if (type === "docx" || type === "doc") return "description";
    if (type === "xlsx" || type === "xls") return "table_chart";
    if (["png", "jpg", "jpeg", "gif"].includes(type)) return "image";
    return "insert_drive_file";
  };

  return (
    <Dialog open={props.open} onClose={close} maxWidth="md" fullWidth>
      <DialogTitle
        sx={{
          fontWeight: 600,
          fontSize: "16px",
          opacity: 0.79,
          color: "#333",
        }}
      >
        {props.data
          ? t("userSystem.editReleaseNote")
          : t("userSystem.addReleaseNote")}
      </DialogTitle>
      <DialogContent
        sx={{
          overflowY: "auto",
          "&::-webkit-scrollbar": { width: "8px" },
          "&::-webkit-scrollbar-thumb": {
            backgroundColor: "#888",
            borderRadius: "4px",
          },
          "&::-webkit-scrollbar-thumb:hover": { backgroundColor: "#555" },
          "&::-webkit-scrollbar-track": { background: "#f1f1f1" },
        }}
      >
        <div className="flex gap-4 items-center mt-2">
          <TextField
            label={t("userSystem.releaseTitle")}
            variant="outlined"
            name="title"
            color="success"
            value={formData.title}
            onChange={handleChange}
            size="small"
            required
            error={isError && !formData.title}
            sx={{ flex: 1 }}
          />

          <TextField
            label={t("userSystem.releaseVersion") + " *"}
            variant="outlined"
            name="version"
            color="success"
            value={formData.version}
            onChange={handleChange}
            size="small"
            required
            error={isError && !formData.version}
            sx={{ width: 200 }}
          />

          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel size="small" color="success">
              {t("userSystem.status")}
            </InputLabel>
            <Select
              size="small"
              value={formData.status || "draft"}
              onChange={handleChange}
              name="status"
              label={t("userSystem.status")}
              color="success"
              disabled={!props.data}
              sx={{ height: 40 }}
            >
              <MenuItem value="draft">
                <ListItemText primary={t("userSystem.statusDraft")} />
              </MenuItem>
              <MenuItem value="publish">
                <ListItemText primary={t("userSystem.statusPublish")} />
              </MenuItem>
            </Select>
          </FormControl>
        </div>

        <TextField
          label={t("userSystem.releaseContent")}
          variant="outlined"
          margin="normal"
          name="content"
          color="success"
          value={formData.content}
          onChange={handleChange}
          fullWidth
          multiline
          rows={6}
          required
          error={isError && !formData.content}
        />

        <div className="mt-4">
          <h3 className="text-base font-bold text-[#00463B] mb-2">
            {t("releases.attachments")}
          </h3>

          {attachments.length > 0 && (
            <div className="flex flex-col gap-2 mb-3">
              {attachments.map((filePath, index) => {
                const fileName = filePath.split("/").pop() || filePath;
                const ext = fileName.split(".").pop() || "";
                return (
                  <div
                    key={index}
                    className="flex items-center text-[#333]"
                    title={t("releases.attachments") || ""}
                  >
                    <span className="material-icons text-[20px] text-[#00463B] mr-2">
                      {getFileIcon(ext)}
                    </span>
                    <span className="text-[#666]">{fileName}</span>
                    <IconButton
                      size="small"
                      onClick={() =>
                        setAttachments((prev) =>
                          prev.filter((_, i) => i !== index)
                        )
                      }
                      className="!ml-2 !text-[#d32f2f]"
                    >
                      <span className="material-icons text-[16px]">close</span>
                    </IconButton>
                  </div>
                );
              })}
            </div>
          )}

          <div className="flex items-center gap-3">
            <Button
              variant="outlined"
              color="success"
              size="small"
              startIcon={
                <span className="material-icons text-[20px]">upload_file</span>
              }
              disabled={uploading}
              onClick={handleUploadClick}
            >
              {uploading
                ? `${t("buttons.uploadFile")}...`
                : t("buttons.uploadFile")}
            </Button>
            <input
              ref={fileInputRef}
              type="file"
              multiple
              className="hidden"
              onChange={handleFileChange}
            />
          </div>
        </div>
      </DialogContent>
      <DialogActions
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          padding: "16px 24px",
        }}
      >
        <Button onClick={reset} color="success">
          {t("buttons.reset")}
        </Button>

        <div style={{ marginLeft: "auto" }}>
          <Button onClick={close} color="success">
            {t("buttons.cancel")}
          </Button>
          <Button onClick={handleSave} color="success" variant="contained">
            {t("buttons.save")}
          </Button>
        </div>
      </DialogActions>
    </Dialog>
  );
}

export default ReleaseEditModal;
